/**
 * Reset Password Page
 * Handles password reset from Firebase email link (oobCode)
 */

import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { resetPassword: sendReset } = useAuth();

  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    let active = true;
    async function verify() {
      if (!oobCode || mode !== 'resetPassword') {
        setError('Invalid or missing reset link.');
        setVerifying(false);
        return;
      }
      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        if (!active) return;
        setVerifiedEmail(email);
      } catch (e) {
        console.error('verifyPasswordResetCode error', e);
        setError('This reset link is invalid or has expired.');
      } finally {
        if (active) setVerifying(false);
      }
    }
    verify();
    return () => { active = false; };
  }, [oobCode, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password.length < 6) {
      setError('Password should be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      toast.success('Password has been reset');
      // Try auto sign-in if we know the email
      if (verifiedEmail) {
        try {
          await signInWithEmailAndPassword(auth, verifiedEmail, password);
          setDone(true);
          setTimeout(() => navigate('/', { replace: true }), 500);
          return;
        } catch (e) {
          // fall through to sign-in page
        }
      }
      setDone(true);
      setTimeout(() => navigate('/auth/signin', { replace: true }), 800);
    } catch (e) {
      console.error('confirmPasswordReset error', e);
      setError(e?.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">FinList</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Set a new password</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>
              {verifying ? 'Validating your reset link…' : (verifiedEmail ? `Reset password for ${verifiedEmail}` : 'Unable to validate reset link')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {verifying ? (
              <div className="text-sm text-gray-600">Please wait…</div>
            ) : error && !verifiedEmail ? (
              <div className="space-y-4">
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
                <div className="space-y-3">
                  <form
                    onSubmit={async (e)=>{
                      e.preventDefault();
                      const data = new FormData(e.currentTarget);
                      const email = (data.get('email')||'').toString().trim();
                      if (!email) return;
                      try {
                        await sendReset(email);
                      } catch (_) {}
                    }}
                    className="flex items-center gap-2"
                  >
                    <input
                      name="email"
                      type="email"
                      className="flex-1 rounded border px-3 py-2 text-sm"
                      placeholder="Enter your email to resend"
                      autoComplete="email"
                    />
                    <Button type="submit" variant="outline" size="sm">Resend link</Button>
                  </form>
                  <div className="flex items-center justify-between">
                    <Link to="/auth/forgot-password" className="text-blue-600 hover:text-blue-500 text-sm">Forgot Password</Link>
                    <Link to="/auth/signin" className="text-sm text-gray-600 hover:text-gray-800">Back to sign in</Link>
                  </div>
                </div>
              </div>
            ) : done ? (
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-700">Password reset successful. Redirecting…</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="password"
                  type="password"
                  label="New Password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e)=> setPassword(e.target.value)}
                  disabled={submitting}
                  autoComplete="new-password"
                />
                <Input
                  name="confirm"
                  type="password"
                  label="Confirm Password"
                  placeholder="Re-enter new password"
                  value={confirm}
                  onChange={(e)=> setConfirm(e.target.value)}
                  disabled={submitting}
                  autoComplete="new-password"
                />
                {error && <div className="text-sm text-red-600">{error}</div>}
                <Button type="submit" className="w-full" disabled={submitting} loading={submitting}>
                  Update Password
                </Button>
                <div className="text-center">
                  <Link to="/auth/signin" className="text-blue-600 hover:text-blue-500 text-sm">Back to sign in</Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
